import { useState, useEffect } from 'react';
import api from '../services/api';

export interface PublicConfig {
  qty_rounding_direction: 'floor' | 'ceil';
  min_order_amount: number;
  support_email: string;
  support_phone: string;
  rma_return_window_months: number;
}

const DEFAULT: PublicConfig = {
  qty_rounding_direction: 'floor',
  min_order_amount: 0,
  support_email: '',
  support_phone: '',
  // La Finestra di reso: 12 mesi fissi (decisione del 2026-10-02), resi
  // sempre uguali dal backend. Non è più un'impostazione.
  rma_return_window_months: 12,
};

// Cache modulo-level: l'API viene chiamata al massimo una volta per caricamento pagina
let cached: PublicConfig | null = null;
let fetching: Promise<PublicConfig> | null = null;

export function usePublicConfig(): PublicConfig {
  return usePublicConfigState().config;
}

/** Come ``usePublicConfig``, ma dice anche se la configurazione è arrivata.
 *
 * Serve a chi usa un valore di config per *costruire una richiesta*: partire
 * col default e poi rifare la chiamata quando il valore vero arriva significa
 * due richieste e un risultato momentaneamente sbagliato in pagina. Chi usa
 * la config solo per rendere del testo può continuare con ``usePublicConfig``.
 */
export function usePublicConfigState(): { config: PublicConfig; loaded: boolean } {
  const [config, setConfig] = useState<PublicConfig>(cached ?? DEFAULT);
  const [loaded, setLoaded] = useState<boolean>(cached !== null);

  useEffect(() => {
    if (cached) return;
    if (!fetching) {
      fetching = api
        .get<PublicConfig>('/api/erp/public/site-settings')
        .then(r => r.data)
        .catch(() => DEFAULT);
    }
    let alive = true;
    fetching.then(cfg => {
      cached = cfg;
      if (!alive) return;
      setConfig(cfg);
      setLoaded(true);
    });
    return () => { alive = false; };
  }, []);

  return { config, loaded };
}

/** Chiamare dopo aver salvato le impostazioni in admin per forzare il refresh al prossimo uso. */
export function invalidatePublicConfig(): void {
  cached = null;
  fetching = null;
}
